import React from "react";
import {
  TextField,
  MenuItem,
  Select,
  FormControl,
  Box,
  Typography,
} from "@mui/material";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import theme from "./theme"; // Import theme for consistency

export const inputStyles = {
  formContainer: {
    backgroundColor: theme.inputs.background,
    padding: 16,
    borderRadius: theme.borderRadius.md,
    width: "100%",
  },

  // Stand-alone labels (e.g. <Typography sx={inputStyles.label}>)
  label: {
    color: theme.inputs.textColor,
    fontSize: theme.typography.fontSize.sm,
    marginBottom: "4px",
    "&.Mui-focused": {
      color: theme.inputs.textColor,
    },
    "&.Mui-disabled": {
      color: theme.textColors.dark.disabled, // grey when disabled
    },
  },

  // When you’re using MUI’s <InputLabel> inside TextField / FormControl
  inputLabel: {
    color: theme.inputs.textColor,
    fontSize: theme.typography.fontSize.md,
    "&.Mui-focused": {
      color: theme.inputs.textColor,
    },
    "&.Mui-disabled": {
      color: theme.textColors.dark.disabled, // grey when disabled
    },
  },

  textField: {
    // Override the label inside TextField
    "& .MuiInputLabel-root": {
      color: theme.inputs.textColor,
      fontSize: theme.typography.fontSize.md,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.inputs.textColor,
    },

    // The outlined input itself
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.inputs.background,
      borderRadius: theme.borderRadius.sm,
      fontSize: theme.typography.fontSize.md,
      color: theme.inputs.textColor,
      "& fieldset": {
        borderColor: theme.inputs.borderColor,
      },
      "&:hover fieldset": {
        borderColor: theme.inputs.hoverBorderColor,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.inputs.focusBorderColor,
      },
      "&.Mui-disabled": {
        color: theme.textColors.dark.disabled,
        backgroundColor: theme.backgroundColors.dark.tableButton.disabled,
      },
      "&.Mui-disabled fieldset": {
        borderColor: theme.textColors.dark.disabled,
      },
    },
    "& .MuiInputLabel-root.Mui-disabled": {
      color: theme.textColors.dark.disabled,
    },

    // helper-text color override
    "& .MuiFormHelperText-root": {
      color: theme.inputs.textColor,
    },
    // the actual <input> element
    input: {
      padding: "10px",
      color: theme.inputs.textColor,
    },
    marginBottom: 2,
    "& .MuiOutlinedInput-input::placeholder": {
      fontSize: theme.typography.fontSize.sm,
    },
    "& input:-webkit-autofill": {
      WebkitBoxShadow: `0 0 0 1000px ${theme.inputs.background} inset`,
      WebkitTextFillColor: theme.inputs.textColor,
    },
  },

  select: {
    backgroundColor: theme.inputs.background,
    borderRadius: theme.borderRadius.sm,
    fontSize: theme.typography.fontSize.md,
    color: theme.inputs.textColor,
    padding: "8px 4px",
    "& .MuiSelect-select img": {
      height: "1em",
      width: "1rem",
      verticalAlign: "middle",
    },

    // Keep the floating label white if you ever wrap Select in FormControl+InputLabel
    "& .MuiInputLabel-root": {
      color: theme.inputs.textColor,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.inputs.textColor,
    },

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.inputs.borderColor,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.inputs.hoverBorderColor,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.inputs.focusBorderColor,
    },

    // apply on the outer InputBase root
    "&.Mui-disabled": {
      backgroundColor: theme.backgroundColors.dark.tableButton.disabled,
    },

    // 1) grey out the text in the inner div
    "& .MuiSelect-select.Mui-disabled": {
      color: theme.textColors.dark.disabled,
    },

    // 2) grey out the outline border when disabled
    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.textColors.dark.disabled,
    },

    // 3) grey out the arrow icon when disabled
    "&.Mui-disabled .MuiSvgIcon-root": {
      color: theme.textColors.dark.disabled,
    },

    "& .MuiSvgIcon-root": {
      color: theme.inputs.iconColor,
      fontSize: "12px",
    },
    "& .MuiSelect-select": {
      padding: "4px 8px",
    },
  },

  selectMenuProps: {
    PaperProps: {
      sx: {
        // dark popup background + text
        bgcolor: theme.inputs.background,
        color: theme.inputs.textColor,
        fontSize: theme.typography.fontSize.sm,
        // make every MenuItem match
        "& .MuiMenuItem-root": {
          bgcolor: theme.inputs.background,
          fontSize: theme.typography.fontSize.sm,
          "&:hover": {
            bgcolor: theme.inputs.hoverBorderColor,
            fontSize: theme.typography.fontSize.sm,
          },
          "&.Mui-selected": {
            bgcolor: theme.inputs.focusBorderColor,
            fontSize: theme.typography.fontSize.sm,
          },
        },
      },
    },
    // if you need to adjust the <ul> itself:
    MenuListProps: {
      sx: {
        bgcolor: "inherit",
      },
    },
  },

  checkbox: {
    // styles applied to <Checkbox sx={…} />
    control: {
      color: theme.inputs.textColor, // unchecked circle
      "&.Mui-checked": {
        color: theme.main.primaryBlue, // checked fill
      },
      "&:hover": {
        backgroundColor: `${theme.main.primaryBlue}20`, // subtle hover
      },
    },
    // styles applied to <FormControlLabel sx={…} />
    label: {
      color: theme.inputs.textColor,
      fontSize: theme.typography.fontSize.md,
      marginTop: "8px",
      fontFamily: theme.typography.fontFamily,
      userSelect: "none",
    },
  },

  informationText: {
    fontSize: "12px",
    color: theme.textColors.dark.primary,
    marginBottom: "10px",
    mt: "5px",
  },

  toggleButtonStyles: {
    fontSize: "0.7rem",
    bgcolor: theme.backgroundColors.dark.primary,
    color: theme.textColors.dark.primary,
    mx: 0.5,
    "&.Mui-selected": {
      fontWeight: "bold",
      bgcolor: theme.main.secondaryBlue,
      color: theme.textColors.dark.primary,
      border: `2px solid ${theme.border.light}`,
      "&:hover": {
        fontWeight: "bold",
        color: theme.textColors.dark.primary,
        bgcolor: theme.main.secondaryBlue,
      },
    },
    "&:hover": {
      fontWeight: "bold",
      color: theme.textColors.dark.primary,
      bgcolor: theme.main.secondaryBlue,
    },
  },
};

export const radioGroupStyles = {
  container: {
    "& .MuiFormControlLabel-root": {
      marginRight: "24px",
    },
  },
  radio: {
    color: theme.textColors.dark.primary,
    "&.Mui-checked": { color: theme.main.primaryBlue },
    "&:hover": { backgroundColor: `${theme.main.primaryBlue}20` },
  },
  formControlLabel: {
    alignItems: "center",
    "& .MuiFormControlLabel-label": {
      color: theme.inputs.textColor,
      fontSize: theme.typography.fontSize.sm,
      fontFamily: theme.typography.fontFamily,
      textTransform: "capitalize",
      userSelect: "none",
    },
  },
};

// const StyledForm = () => {
//   return (
//     <Box sx={inputStyles.formContainer}>
//       {/* Name Field */}
//       <Typography sx={inputStyles.label}>
//         Name <span style={{ opacity: 0.6 }}>Optional</span>
//       </Typography>
//       <TextField
//         variant="outlined"
//         fullWidth
//         defaultValue="My Test Key"
//         sx={inputStyles.textField}
//       />

//       {/* Project Dropdown */}
//       <Typography sx={inputStyles.label}>Project</Typography>
//       <FormControl fullWidth>
//         <Select
//           defaultValue="default"
//           variant="outlined"
//           IconComponent={UnfoldMoreIcon} // Custom dropdown icon
//           sx={inputStyles.select}
//         >
//           <MenuItem value="default">Default project</MenuItem>
//         </Select>
//       </FormControl>
//     </Box>
//   );
// };

// export default StyledForm;
